<template>
  <div
    class="bg-gradient-to-br from-(--p-primary-500) to-(--p-primary-600) shadow-lg sticky top-0 z-[1000]"
  >
    <Toast
      position="top-right"
      :life="4000"
      :showTransformOptions="true"
      :showCloseIcon="true"
      class="custom-toast"
    >
      <template #message="slotProps">
        <div class="flex items-start gap-2 sm:gap-3 p-1">
          <div class="shrink-0 mt-0.5">
            <i
              :class="getToastIcon(slotProps.message.severity)"
              :style="{ color: getToastColor(slotProps.message.severity) }"
              class="!text-base sm:!text-lg"
            ></i>
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2 mb-1">
              <h4 class="font-semibold text-sm text-(--p-primary-500) leading-tight line-clamp-1">
                {{ slotProps.message.summary }}
              </h4>
              <span class="text-[10px] sm:text-xs text-(--p-primary-500) shrink-0">
                {{ formatToastTime(slotProps.message.timestamp) }}
              </span>
            </div>
            <p class="text-xs sm:text-sm text-(--p-primary-400) leading-relaxed">
              {{ slotProps.message.detail }}
            </p>
          </div>
        </div>
      </template>
    </Toast>

    <Menubar :model="items" class="custom-menubar" :class="{ 'hide-hamburger': isHomePage }">
      <template #start>
        <img
          src="/stellantis-logo.png"
          alt="Stellantis Logo"
          class="max-w-[100px] sm:max-w-[120px] lg:max-w-[140px] mr-2 sm:mr-4 lg:mr-6"
        />
      </template>

      <template #item="{ item }">
        <template v-if="state.isAuthenticated">
          <Button
            type="button"
            :class="[
              'text-white font-medium transition-all duration-200 ease-out relative text-sm sm:text-base nav-button',
            ]"
            @click="router.push(item.route)"
          >
            <span>{{ item.label }}</span>
          </Button>
        </template>
      </template>

      <template v-if="state.isAuthenticated" #end>
        <div class="flex items-center gap-1 sm:gap-2">
          <div
            class="hidden lg:flex items-center gap-3 px-3 py-2 bg-white/10 rounded-lg backdrop-blur-sm"
          >
            <div class="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <i class="pi pi-user text-white !text-sm"></i>
            </div>
            <div class="flex flex-col">
              <span class="text-xs text-white font-medium">
                {{ (state.user as NavBarUser)?.forename }}
                {{ (state.user as NavBarUser)?.surname }}
              </span>
              <span class="text-[10px] text-white/70">
                {{ (state.user as NavBarUser)?.username }}
              </span>
            </div>
          </div>

          <div class="relative">
            <Button
              type="button"
              icon="pi pi-bell"
              class="!text-white w-10 h-10 sm:w-11 sm:h-11 transition-all duration-200 ease-out hover:bg-white/15 active:scale-95 sm:hover:scale-105"
              :class="{ 'animate-pulse': notifications?.length > 0 }"
              @click="notificationToggle"
              aria-haspopup="true"
              aria-controls="notification_popover"
              v-tooltip.bottom="'Notifications'"
              text
              rounded
            />
            <span
              v-if="notifications?.length > 0"
              class="absolute -top-1 -right-1 bg-gradient-to-br from-red-500 to-red-600 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1 shadow-md shadow-red-500/40 animate-bounce-in"
            >
              {{ notifications.length > 9 ? '9+' : notifications.length }}
            </span>
          </div>

          <Button
            v-if="!isMobile"
            type="button"
            icon="pi pi-question-circle"
            class="!text-white w-10 h-10 sm:w-11 sm:h-11 transition-all duration-200 ease-out hover:bg-white/15 active:scale-95 sm:hover:scale-105"
            @click="openUserGuide"
            aria-label="Open user guide"
            v-tooltip.bottom="'Help & User Guide'"
            text
            rounded
          />

          <Popover ref="notification" class="custom-popover">
            <div
              class="flex flex-col w-[calc(100vw-2rem)] sm:w-[24rem] lg:w-[28rem] max-w-[28rem] max-h-[70vh] sm:max-h-[28rem] lg:max-h-[32rem]"
            >
              <div
                class="flex items-center justify-between p-3 sm:p-4 border-b border-(--p-surface-200) shrink-0"
              >
                <div class="flex items-center gap-2">
                  <i class="pi pi-bell text-(--p-primary-600) !text-base sm:!text-lg"></i>
                  <h3 class="text-base sm:text-lg font-semibold text-(--p-anthracite-500)">
                    Notifications
                  </h3>
                </div>
                <div class="flex items-center gap-2">
                  <span
                    class="text-xs sm:text-sm text-(--p-anthracite-500) bg-(--p-surface-100) px-2 py-0.5 rounded-full"
                  >
                    {{ notifications?.length || 0 }}
                  </span>
                  <button
                    v-if="notifications?.length"
                    @click="clearNotifications"
                    class="p-2 rounded-lg hover:bg-(--p-surface-100) transition-colors active:scale-95"
                    title="Clear all notifications"
                  >
                    <i class="pi pi-trash text-(--p-anthracite-500) !text-sm"></i>
                  </button>
                </div>
              </div>

              <div class="flex-1 overflow-y-auto overscroll-contain">
                <template v-if="notifications?.length">
                  <div
                    v-for="(notif, index) in notifications"
                    :key="index"
                    class="p-3 sm:p-4 border-b border-(--p-surface-100) hover:bg-(--p-surface-50) active:bg-(--p-surface-100) transition-colors"
                  >
                    <div class="flex items-start gap-3">
                      <div class="shrink-0 mt-0.5">
                        <div
                          class="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center"
                          :class="{
                            'bg-green-100 text-green-600': notif.severity === 'success',
                            'bg-blue-100 text-blue-600': notif.severity === 'info',
                            'bg-yellow-100 text-yellow-600': notif.severity === 'warn',
                            'bg-red-100 text-red-600': notif.severity === 'error',
                          }"
                        >
                          <i
                            class="pi !text-xs sm:!text-sm"
                            :class="{
                              'pi-check': notif.severity === 'success',
                              'pi-info-circle': notif.severity === 'info',
                              'pi-exclamation-triangle': notif.severity === 'warn',
                              'pi-times-circle': notif.severity === 'error',
                            }"
                          ></i>
                        </div>
                      </div>

                      <div class="flex-1 min-w-0">
                        <div class="flex items-start justify-between gap-2">
                          <h4 class="font-semibold text-(--p-anthracite-500) text-sm leading-tight">
                            {{ notif.summary }}
                          </h4>
                          <span class="text-[10px] sm:text-xs text-(--p-anthracite-500) shrink-0">
                            {{ formatTime(notif.timestamp) }}
                          </span>
                        </div>
                        <p class="text-xs sm:text-sm text-(--p-anthracite-500) mt-1 leading-relaxed">
                          {{ notif.detail }}
                        </p>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="flex flex-col items-center justify-center py-10 sm:py-12 px-4">
                    <div
                      class="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-(--p-surface-100) flex items-center justify-center mb-3 sm:mb-4"
                    >
                      <i class="pi pi-bell text-(--p-anthracite-500) !text-xl sm:!text-2xl"></i>
                    </div>
                    <h4
                      class="text-(--p-anthracite-500) font-medium mb-1 sm:mb-2 text-sm sm:text-base"
                    >
                      No notifications
                    </h4>
                    <p class="text-xs sm:text-sm text-(--p-anthracite-500) text-center max-w-[200px]">
                      You're all caught up! New notifications will appear here.
                    </p>
                  </div>
                </template>
              </div>

              <div
                v-if="notifications?.length"
                class="p-2 sm:p-3 border-t border-(--p-surface-200) bg-(--p-surface-50) shrink-0"
              >
                <button
                  @click="clearNotifications"
                  class="w-full py-2.5 sm:py-2 px-3 text-sm text-(--p-anthracite-500) hover:text-(--p-anthracite-500) hover:bg-(--p-surface-100) active:bg-(--p-surface-200) rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <i class="pi pi-trash !text-sm"></i>
                  Clear all notifications
                </button>
              </div>
            </div>
          </Popover>

          <Button
            type="button"
            icon="pi pi-cog"
            class="w-10 h-10 sm:w-11 sm:h-11 transition-all duration-200 ease-out hover:bg-white/15 active:scale-95 sm:hover:scale-105"
            :pt:icon="{ class: 'text-white' }"
            @click="openSettings($event)"
            aria-label="Open settings"
            v-tooltip.bottom="'Settings'"
            text
            rounded
          />

          <Popover ref="settingsPopover" class="custom-popover settings-popover">
            <div
              class="flex flex-col w-[calc(100vw-2rem)] sm:w-[22rem] lg:w-[26rem] max-w-[26rem] max-h-[80vh] sm:max-h-[calc(100vh-120px)]"
            >
              <div
                class="flex items-center gap-2 p-3 sm:p-4 border-b-2 border-(--p-surface-200) bg-gradient-to-r from-(--p-surface-0) to-(--p-surface-50) shrink-0"
              >
                <i class="pi pi-cog text-(--p-primary-600) !text-lg"></i>
                <h3 class="text-base sm:text-lg font-bold text-(--p-anthracite-500)">Settings</h3>
              </div>

              <div
                class="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-4 space-y-4"
                tabindex="0"
                role="region"
                aria-label="Settings panel"
              >
                <!-- Temporarily hidden theme switch -->
                <div v-if="false" class="space-y-2">
                  <h4
                    class="text-sm sm:text-[0.9375rem] font-semibold text-(--p-anthracite-500) flex items-center"
                  >
                    <i class="pi pi-palette mr-2 !text-sm"></i>
                    Theme
                  </h4>
                  <Dropdown
                    v-model="themeChoice"
                    :options="themeOptions"
                    optionLabel="label"
                    optionValue="value"
                    class="w-full"
                    @update:modelValue="applyTheme"
                  />
                </div>

                <Divider v-if="false" class="!my-3" />

                <div class="space-y-3">
                  <h4
                    class="text-sm sm:text-[0.9375rem] font-semibold text-(--p-anthracite-500) flex items-center"
                  >
                    <i class="pi pi-eye mr-2 !text-sm"></i>
                    Accessibility
                  </h4>

                  <div class="space-y-2">
                    <h5 class="text-xs sm:text-sm font-medium text-(--p-anthracite-500)">Visual</h5>
                    <div
                      class="flex items-center justify-between py-1.5 sm:py-1 min-h-[44px] sm:min-h-0"
                    >
                      <span class="text-sm">High contrast</span>
                      <ToggleSwitch
                        :modelValue="highContrast"
                        @update:modelValue="toggleHighContrast()"
                      />
                    </div>
                    <div
                      class="flex items-center justify-between py-1.5 sm:py-1 min-h-[44px] sm:min-h-0"
                    >
                      <span class="text-sm">Color blind support</span>
                      <ToggleSwitch
                        :modelValue="colorBlindSupport"
                        @update:modelValue="toggleColorBlindSupport()"
                      />
                    </div>
                  </div>

                  <div class="space-y-2">
                    <h5 class="text-xs sm:text-sm font-medium text-(--p-anthracite-500)">Typography</h5>
                    <div
                      class="flex items-center justify-between py-1.5 sm:py-1 min-h-[44px] sm:min-h-0"
                    >
                      <span class="text-sm">Dyslexia friendly</span>
                      <ToggleSwitch
                        :modelValue="dyslexiaFriendly"
                        @update:modelValue="toggleDyslexiaFriendly()"
                      />
                    </div>
                    <div class="space-y-1">
                      <label class="text-xs sm:text-sm text-(--p-anthracite-500)">Font size</label>
                      <Dropdown
                        :modelValue="fontSize"
                        :options="[
                          { label: 'Small', value: 'small' },
                          { label: 'Medium', value: 'medium' },
                          { label: 'Large', value: 'large' },
                          { label: 'Extra Large', value: 'extra-large' },
                        ]"
                        optionLabel="label"
                        optionValue="value"
                        @update:modelValue="setFontSize"
                        class="w-full"
                      />
                    </div>
                    <div class="space-y-1">
                      <label class="text-xs sm:text-sm text-(--p-anthracite-500)">Line height</label>
                      <Dropdown
                        :modelValue="lineHeight"
                        :options="[
                          { label: 'Tight', value: 'tight' },
                          { label: 'Normal', value: 'normal' },
                          { label: 'Relaxed', value: 'relaxed' },
                        ]"
                        optionLabel="label"
                        optionValue="value"
                        @update:modelValue="setLineHeight"
                        class="w-full"
                      />
                    </div>
                    <div class="space-y-1">
                      <label class="text-xs sm:text-sm text-(--p-anthracite-500)"
                        >Letter spacing</label
                      >
                      <Dropdown
                        :modelValue="letterSpacing"
                        :options="[
                          { label: 'Tight', value: 'tight' },
                          { label: 'Normal', value: 'normal' },
                          { label: 'Wide', value: 'wide' },
                        ]"
                        optionLabel="label"
                        optionValue="value"
                        @update:modelValue="setLetterSpacing"
                        class="w-full"
                      />
                    </div>
                  </div>

                  <div class="space-y-2">
                    <h5 class="text-xs sm:text-sm font-medium text-(--p-anthracite-500)">Navigation</h5>
                    <div
                      class="flex items-center justify-between py-1.5 sm:py-1 min-h-[44px] sm:min-h-0"
                    >
                      <span class="text-sm">Focus indicators</span>
                      <ToggleSwitch
                        :modelValue="focusIndicators"
                        @update:modelValue="toggleFocusIndicators()"
                      />
                    </div>
                    <div
                      class="flex items-center justify-between py-1.5 sm:py-1 min-h-[44px] sm:min-h-0"
                    >
                      <span class="text-sm">Keyboard navigation</span>
                      <ToggleSwitch
                        :modelValue="keyboardNavigation"
                        @update:modelValue="toggleKeyboardNavigation()"
                      />
                    </div>
                    <div
                      class="flex items-center justify-between py-1.5 sm:py-1 min-h-[44px] sm:min-h-0"
                    >
                      <span class="text-sm">Screen reader optimised</span>
                      <ToggleSwitch
                        :modelValue="screenReaderOptimized"
                        @update:modelValue="toggleScreenReaderOptimized()"
                      />
                    </div>
                  </div>

                  <div class="space-y-2">
                    <h5 class="text-xs sm:text-sm font-medium text-(--p-anthracite-500)">Motion</h5>
                    <div
                      class="flex items-center justify-between py-1.5 sm:py-1 min-h-[44px] sm:min-h-0"
                    >
                      <span class="text-sm">Reduced motion</span>
                      <ToggleSwitch
                        :modelValue="reducedMotion"
                        @update:modelValue="toggleReducedMotion()"
                      />
                    </div>
                  </div>
                </div>

                <template v-if="hasEmulationAccess || emulating">
                  <Divider class="!my-3" />
                  <div class="space-y-2">
                    <h4
                      class="text-sm sm:text-[0.9375rem] font-semibold text-(--p-anthracite-500) flex items-center"
                    >
                      <i class="pi pi-users mr-2 !text-sm"></i>
                      User Emulation
                    </h4>
                    <template v-if="!emulating">
                      <AutoComplete
                        v-model="selectedItem"
                        :suggestions="filteredItems"
                        @complete="searchEmulations"
                        :virtualScrollerOptions="{ itemSize: 70 }"
                        :optionLabel="(option) => option.forename + ' ' + option.surname"
                        placeholder="Search by DNumber, email or name"
                        dropdown
                        :pt:root="{ class: 'w-full' }"
                        @option-select="activateEmulation"
                      >
                        <template #option="slotProps">
                          <div class="py-1">
                            <div class="text-sm font-medium">
                              {{ slotProps.option.forename + ' ' + slotProps.option.surname }}
                            </div>
                            <small class="text-xs text-(--p-anthracite-500)">
                              ({{ slotProps.option.username }})
                              {{ slotProps.option.role?.label }}
                            </small>
                          </div>
                        </template>
                      </AutoComplete>
                    </template>
                    <template v-else>
                      <div class="flex items-center justify-between py-1.5 min-h-[44px] sm:min-h-0">
                        <span class="text-sm text-(--p-anthracite-500)">Emulation active</span>
                        <Button label="Cancel" size="small" @click="resetEmulation" />
                      </div>
                    </template>
                  </div>
                </template>

                <Divider class="!my-3" />

                <div class="space-y-2">
                  <h4
                    class="text-sm sm:text-[0.9375rem] font-semibold text-(--p-anthracite-500) flex items-center"
                  >
                    <i class="pi pi-info-circle mr-2 !text-sm"></i>
                    Legal
                  </h4>
                  <div class="flex flex-col gap-2">
                    <Button
                      label="Cookie Policy"
                      icon="pi pi-shield"
                      severity="secondary"
                      outlined
                      class="w-full min-h-[44px] sm:min-h-0 justify-start"
                      @click="
                        () => {
                          settingsPopover.value?.hide()
                          router.push('/cookie-policy')
                        }
                      "
                    />
                    <Button
                      label="Privacy Policy"
                      icon="pi pi-lock"
                      severity="secondary"
                      outlined
                      class="w-full min-h-[44px] sm:min-h-0 justify-start"
                      @click="
                        () => {
                          settingsPopover.value?.hide()
                          router.push('/privacy-policy')
                        }
                      "
                    />
                  </div>
                </div>

                <Divider class="!my-3" />

                <Button
                  label="Help & User Guide"
                  icon="pi pi-question-circle"
                  severity="secondary"
                  outlined
                  class="w-full sm:hidden min-h-[44px]"
                  @click="openUserGuide"
                />

                <Button
                  label="Logout"
                  icon="pi pi-sign-out"
                  severity="danger"
                  outlined
                  class="w-full min-h-[44px] sm:min-h-0"
                  @click="handleLogout"
                />
              </div>
            </div>
          </Popover>
        </div>
      </template>
    </Menubar>

    <!-- Mobile Menu Popover -->
    <Popover
      v-if="isMobile && state.isAuthenticated"
      ref="mobileMenuPopover"
      class="mobile-menu-popover"
    >
      <div class="flex flex-col w-[calc(100vw-2rem)]">
        <!-- Mobile Menu Items -->
        <div class="py-2">
          <button
            v-for="item in items"
            :key="item.route"
            @click="handleMobileNavClick(item.route)"
            class="mobile-menu-item w-full px-4 py-4 text-(--p-primary-500) flex items-center justify-between"
            :class="{
              'bg-(--p-primary-500) text-white font-semibold': isActiveRoute(item.route),
            }"
          >
            <span class="flex items-center gap-3">
              <i :class="item.icon" class="text-lg"></i>
              <span class="text-base">{{ item.label }}</span>
            </span>
            <i class="pi pi-chevron-right text-sm opacity-80"></i>
          </button>
        </div>
      </div>
    </Popover>

    <UserGuide ref="userGuide" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import Blue from '../config/themes/Blue'
import Anthracite from '../config/themes/Anthracite'
import White from '../config/themes/White'
import { updatePreset } from '@primeuix/themes'
import useAuth from '@/composables/Auth'
import useNotifications from '@/composables/Notification'
import useAccessibility from '@/composables/Accessibility'
import { formatRelativeTime } from '@/utils/formatters'
import type { NavBarUser, AutoCompleteEvent, AutoCompleteSelectEvent } from '@/config/types/NavBar'

const { state, getUsers, getUser, emulating, logout: logoutUser } = useAuth()
const { toast, notifications, clearNotifications } = useNotifications()
const {
  highContrast,
  reducedMotion,
  focusIndicators,
  keyboardNavigation,
  screenReaderOptimized,
  colorBlindSupport,
  dyslexiaFriendly,
  fontSize,
  lineHeight,
  letterSpacing,
  toggleHighContrast,
  toggleReducedMotion,
  toggleFocusIndicators,
  toggleKeyboardNavigation,
  toggleScreenReaderOptimized,
  toggleColorBlindSupport,
  toggleDyslexiaFriendly,
  setFontSize,
  setLineHeight,
  setLetterSpacing,
} = useAccessibility()
const router = useRouter()

const isHomePage = computed(() => router.currentRoute.value.path === '/')

const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
const isMobile = computed(() => windowWidth.value < 1024)
const mobileMenuPopover = ref()
const mobileMenuOpen = ref(false)

const handleResize = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
    // Close mobile menu when resizing to desktop
    if (windowWidth.value >= 1024 && mobileMenuPopover.value) {
      mobileMenuPopover.value.hide()
      mobileMenuOpen.value = false
    }
  }
}

const toggleMobileMenu = (event: Event) => {
  if (isMobile.value && mobileMenuPopover.value) {
    // Get the hamburger button as the target
    const hamburgerButton = document.querySelector(
      '.custom-menubar .p-menubar-button',
    ) as HTMLElement
    if (hamburgerButton) {
      mobileMenuOpen.value = !mobileMenuOpen.value

      // Use the hamburger button as the target for the popover
      if (mobileMenuOpen.value) {
        mobileMenuPopover.value.show(event, hamburgerButton)
      } else {
        mobileMenuPopover.value.hide()
      }

      // Hide default PrimeVue menu
      setTimeout(() => {
        const rootList = document.querySelector(
          '.custom-menubar .p-menubar-root-list',
        ) as HTMLElement
        if (rootList) {
          rootList.style.display = 'none'
        }
      }, 0)
    }
  }
}

const handleMobileNavClick = (route: string) => {
  router.push(route)
  if (mobileMenuPopover.value) {
    mobileMenuPopover.value.hide()
    mobileMenuOpen.value = false
  }
}

// Watch for hamburger clicks on mobile
let hamburgerClickHandler: ((e: Event) => void) | null = null

onMounted(() => {
  if (typeof window !== 'undefined') {
    // Setup hamburger click listener
    const setupHamburgerListener = () => {
      const hamburgerButton = document.querySelector(
        '.custom-menubar .p-menubar-button',
      ) as HTMLElement
      if (hamburgerButton && !hamburgerClickHandler) {
        hamburgerClickHandler = (e: Event) => {
          if (isMobile.value) {
            e.preventDefault()
            e.stopPropagation()
            toggleMobileMenu(e)
          }
        }
        hamburgerButton.addEventListener('click', hamburgerClickHandler, true)
      }
    }

    // Setup listener after DOM is ready
    nextTick(() => {
      setupHamburgerListener()
      setTimeout(setupHamburgerListener, 200)
      setTimeout(setupHamburgerListener, 500)
    })

    // Watch for menu visibility changes as fallback
    const observer = new MutationObserver(() => {
      if (isMobile.value && mobileMenuOpen.value === false) {
        const rootList = document.querySelector(
          '.custom-menubar .p-menubar-root-list',
        ) as HTMLElement
        if (rootList && rootList.style.display !== 'none' && rootList.offsetParent !== null) {
          const hamburgerButton = document.querySelector(
            '.custom-menubar .p-menubar-button',
          ) as HTMLElement
          if (hamburgerButton) {
            toggleMobileMenu(new Event('click'))
          }
          rootList.style.display = 'none'
        }
      }
    })

    const menubar = document.querySelector('.custom-menubar')
    if (menubar) {
      observer.observe(menubar, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['style', 'class'],
      })
    }
  }
})

onUnmounted(() => {
  if (hamburgerClickHandler) {
    const hamburgerButton = document.querySelector(
      '.custom-menubar .p-menubar-button',
    ) as HTMLElement
    if (hamburgerButton) {
      hamburgerButton.removeEventListener('click', hamburgerClickHandler, true)
    }
    hamburgerClickHandler = null
  }
})

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
  }
})

// Close mobile menu on route change
watch(
  () => router.currentRoute.value.path,
  () => {
    if (mobileMenuOpen.value && mobileMenuPopover.value) {
      mobileMenuPopover.value.hide()
      mobileMenuOpen.value = false
    }
  },
)

// Watch for popover visibility changes directly
watch(
  () => mobileMenuPopover.value?.visible,
  (isVisible) => {
    if (isVisible !== undefined) {
      mobileMenuOpen.value = isVisible
    }
  },
)

/* eslint-disable */
const hasInsightsAccess = computed(() => {
  const username = (state.user as { username?: string })?.username
  return ['M0017668', 'U008310'].includes(username ?? '')

  // Previous access-level check:
  // const userAccess = (state.user as { access?: number })?.access
  // return userAccess !== undefined && userAccess !== null && [5, 6, 7, 9, 10].includes(userAccess)
})

const hasAdminAccess = computed(() => {
  const userAccess = (state.user as { access?: number })?.access
  return userAccess !== undefined && userAccess !== null && [6, 7, 9, 10].includes(userAccess)
})

const hasEmulationAccess = computed(() => {
  const userAccess = (state.user as { access?: number })?.access
  return userAccess !== undefined && userAccess !== null && [6, 7, 9, 10].includes(userAccess)
})

const items = computed(() => {
  const baseItems = [
    {
      label: 'Reporting',
      icon: 'pi pi-paper',
      route: '/reporting',
    },
    {
      label: 'Retailer Development Actions',
      icon: 'pi pi-graph',
      route: '/action-plans',
    },
  ]

  if (hasInsightsAccess.value) {
    baseItems.push({
      label: 'Insights',
      icon: 'pi pi-chart-line',
      route: '/insights',
    })
  }

  if (hasAdminAccess.value) {
    // baseItems.push({
    //   label: 'Admin',
    //   icon: 'pi pi-shield',
    //   route: '/admin',
    // })
  }

  return baseItems
})

const settingsPopover = ref()
const themeOptions = [
  { label: 'Blue', value: 'Blue' },
  { label: 'Anthracite', value: 'Anthracite' },
  { label: 'White', value: 'White' },
]
const themeChoice = ref<'Blue' | 'Anthracite' | 'White'>('Blue')
const modeChoice = ref<'light' | 'dark'>('light')

const openSettings = (e: Event) => {
  settingsPopover.value.toggle(e)
}

const applyTheme = () => {
  setLightDark(modeChoice.value)
  if (themeChoice.value === 'Blue') updatePreset(Blue)
  else if (themeChoice.value === 'Anthracite') updatePreset(Anthracite)
  else if (themeChoice.value === 'White') updatePreset(White)
}

const selectedItem = ref()
const filteredItems = ref()

const searchEmulations = (event: AutoCompleteEvent) => {
  getUsers(event.query).then((response) => {
    filteredItems.value = response
  })
}

const activateEmulation = (event: AutoCompleteSelectEvent) => {
  getUser(event.value?.username).then((data: NavBarUser) => {
    toast(
      'Emulation Activated',
      `You are now emulating user: ${data.forename} ${data.surname} (${data.username})`,
    )
  })
}

const resetEmulation = () => {
  selectedItem.value = null
  getUser('').then((data: NavBarUser) => {
    toast('Emulation Ended', `Returning to default user settings, ${data.forename}.`)
  })
}

const setLightDark = (theme = 'light') => {
  if (theme == 'light') {
    document.documentElement.classList.remove('dark-selector')
    document.documentElement.classList.remove('bg-[url("/dark_bg.svg")]')
    document.documentElement.classList.add('bg-[url("/light_bg.svg")]')
  } else {
    document.documentElement.classList.add('dark-selector')
    document.documentElement.classList.remove('bg-[url("/light_bg.svg")]')
    document.documentElement.classList.add('bg-[url("/dark_bg.svg")]')
  }
}

const notification = ref()
const userGuide = ref()

const notificationToggle = (event: Event) => {
  notification.value.toggle(event)
}

const openUserGuide = () => {
  userGuide.value.openGuide()
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.ctrlKey && event.key === '/') {
    event.preventDefault()
    openUserGuide()
  }
}

const formatTime = (timestamp: string) => {
  return formatRelativeTime(timestamp, { granularity: 'minutes', nowText: 'Just now' })
}

const getToastIcon = (severity: string) => {
  switch (severity) {
    case 'success':
      return 'pi pi-check-circle'
    case 'info':
      return 'pi pi-info-circle'
    case 'warn':
    case 'warning':
      return 'pi pi-exclamation-triangle'
    case 'error':
      return 'pi pi-times-circle'
    default:
      return 'pi pi-info-circle'
  }
}

const getToastColor = (severity: string) => {
  switch (severity) {
    case 'success':
      return '#10b981'
    case 'info':
      return '#3b82f6'
    case 'warn':
    case 'warning':
      return '#f59e0b'
    case 'error':
      return '#ef4444'
    default:
      return '#6b7280'
  }
}

const formatToastTime = (timestamp: string) => {
  return formatRelativeTime(timestamp, { granularity: 'seconds', nowText: 'now', nowThreshold: 5 })
}

const isActiveRoute = (route: string) => {
  return router.currentRoute.value.path === route
}

const handleLogout = async () => {
  try {
    await logoutUser()
    router.push('/')
    toast('Logged Out', 'You have been successfully logged out.', 'success')
  } catch {
    toast('Error', 'Failed to logout. Please try again.', 'error')
  }
}
</script>

<style scoped>
:deep(.custom-menubar) {
  background: transparent !important;
  border: none !important;
  padding: 0.5rem 0.75rem !important;
}

@media (min-width: 640px) {
  :deep(.custom-menubar) {
    padding: 0.75rem 1rem !important;
  }
}

@media (min-width: 1024px) {
  :deep(.custom-menubar) {
    padding: 0.75rem 1.5rem !important;
  }
}

:deep(.custom-menubar .p-menubar-root-list) {
  gap: 0.25rem;
}

:deep(.custom-menubar .p-menubar-item-content) {
  background: transparent !important;
  position: relative;
  transition: all 0.2s ease;
  box-shadow: none !important;
  border-radius: 0 !important;

  &:after {
    content: '';
    height: 7px;
    width: 100%;
    position: absolute;
    bottom: -16px;
    left: 0;
    transition: all 0.2s ease;
  }

  &:hover,
  &:active {
    background: transparent;
    box-shadow: none !important;
    border-radius: 0 !important;
    &:after {
      background: white;
    }
  }

  .p-button {
    border: 0;
  }
}

@media (min-width: 640px) {
  :deep(.custom-menubar .p-menubar-root-list) {
    gap: 0.5rem;
  }
}

:deep(.hide-hamburger .p-menubar-button) {
  display: none !important;
}

/* Ensure hamburger icon is white */
:deep(.custom-menubar .p-menubar-button) {
  color: white !important;
}

:deep(.custom-menubar .p-menubar-button .p-menubar-button-icon) {
  color: white !important;
}

:deep(.custom-menubar .p-menubar-button:hover) {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

:deep(.custom-menubar .p-menubar-button:focus) {
  outline: 2px solid rgba(255, 255, 255, 0.5) !important;
  outline-offset: 2px !important;
}

/* Move hamburger button to the right on mobile */
@media (max-width: 1023px) {
  :deep(.custom-menubar) {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    position: relative !important;
  }

  :deep(.custom-menubar .p-menubar-start) {
    flex: 0 0 auto !important;
    order: 1 !important;
  }

  :deep(.custom-menubar .p-menubar-root-list) {
    order: 2 !important;
    flex: 1 !important;
    justify-content: center !important;
  }

  :deep(.custom-menubar .p-menubar-end) {
    order: 3 !important;
    flex: 0 0 auto !important;
    display: flex !important;
    align-items: center !important;
    gap: 0.25rem !important;
  }

  :deep(.custom-menubar .p-menubar-button) {
    order: 4 !important;
    margin-left: 0.5rem !important;
  }
}

/* Improved mobile menu design */
:deep(.custom-menubar .p-menubar-root-list) {
  min-width: 200px !important;
  max-width: calc(100vw - 2rem) !important;
}

@media (min-width: 640px) {
  :deep(.custom-menubar .p-menubar-root-list) {
    min-width: 280px !important;
  }
}

:deep(.custom-menubar .p-menubar-root-list .p-menubar-item) {
  margin: 0.25rem 0 !important;
}

:deep(.custom-menubar .p-menubar-root-list .p-menubar-item > .p-menubar-link) {
  padding: 0.75rem 1rem !important;
  border-radius: 8px !important;
  color: var(--p-anthracite-500) !important;
  transition: all 0.2s ease !important;
  font-weight: 500 !important;
}

:deep(.custom-menubar .p-menubar-root-list .p-menubar-item > .p-menubar-link:hover) {
  background-color: var(--p-surface-100) !important;
  color: var(--p-primary-600) !important;
}

:deep(.custom-menubar .p-menubar-root-list .p-menubar-item > .p-menubar-link:focus) {
  outline: 2px solid var(--p-primary-500) !important;
  outline-offset: 2px !important;
}

:deep(
  .custom-menubar .p-menubar-root-list .p-menubar-item.p-menubar-item-active > .p-menubar-link
) {
  background-color: var(--p-primary-50) !important;
  color: var(--p-primary-600) !important;
  font-weight: 600 !important;
}

.dark-selector :deep(.custom-menubar .p-menubar-root-list) {
  background: var(--p-surface-800) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.dark-selector :deep(.custom-menubar .p-menubar-root-list .p-menubar-item > .p-menubar-link) {
  color: var(--p-anthracite-500) !important;
}

.dark-selector :deep(.custom-menubar .p-menubar-root-list .p-menubar-item > .p-menubar-link:hover) {
  background-color: var(--p-surface-700) !important;
  color: var(--p-primary-400) !important;
}

.dark-selector
  :deep(
    .custom-menubar .p-menubar-root-list .p-menubar-item.p-menubar-item-active > .p-menubar-link
  ) {
  background-color: var(--p-primary-900) !important;
  color: var(--p-primary-300) !important;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.animate-bounce-in {
  animation: bounce-in 0.5s ease-out;
}

.custom-toast :deep(.p-toast-message) {
  border-radius: 12px !important;
  box-shadow:
    0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
  backdrop-filter: blur(8px) !important;
  background: rgba(255, 255, 255, 0.95) !important;
  margin-bottom: 8px !important;
  min-width: 280px !important;
  max-width: calc(100vw - 2rem) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

@media (min-width: 640px) {
  .custom-toast :deep(.p-toast-message) {
    min-width: 320px !important;
    max-width: 420px !important;
    margin-bottom: 12px !important;
  }
}

.dark-selector .custom-toast :deep(.p-toast-message) {
  background: rgba(31, 41, 55, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.custom-toast :deep(.p-toast-message-content) {
  padding: 12px !important;
  border: none !important;
}

@media (min-width: 640px) {
  .custom-toast :deep(.p-toast-message-content) {
    padding: 16px !important;
  }
}

.custom-toast :deep(.p-toast-icon-close) {
  color: rgba(107, 114, 128, 0.6) !important;
  transition: color 0.2s ease !important;
}

.custom-toast :deep(.p-toast-icon-close:hover) {
  color: rgba(107, 114, 128, 1) !important;
}

.custom-toast :deep(.p-toast-message-enter-from),
.custom-toast :deep(.p-toast-message-leave-to) {
  opacity: 0 !important;
  transform: translateX(100%) scale(0.95) !important;
}

.custom-toast :deep(.p-toast-message:focus-within) {
  outline: 2px solid var(--p-primary-500) !important;
  outline-offset: 2px !important;
}

.settings-popover :deep(.p-popover) {
  max-width: calc(100vw - 1rem);
}

@media (min-width: 640px) {
  .settings-popover :deep(.p-popover) {
    max-width: 26rem;
  }
}

.dark-selector .settings-popover :deep(.p-popover-content) {
  background: var(--p-surface-900);
}

.a11y-reduced-motion .animate-bounce-in {
  animation: none !important;
}

.a11y-high-contrast .custom-toast :deep(.p-toast-message) {
  border: 2px solid currentColor !important;
  background: var(--p-surface-0) !important;
}

/* Hide default PrimeVue mobile menu on mobile */
@media (max-width: 1023px) {
  :deep(.custom-menubar .p-menubar-root-list) {
    display: none !important;
  }
}

/* Mobile Menu Popover Styles */
.mobile-menu-popover :deep(.p-popover) {
  max-width: calc(100vw - 2rem);
}

@media (min-width: 640px) {
  .mobile-menu-popover :deep(.p-popover) {
    max-width: 20rem;
  }
}

.mobile-menu-item {
  min-height: 56px;
}

.mobile-menu-item:active {
  background-color: var(--p-primary-700) !important;
}
</style>

<style>
/* Global styles for nav buttons - needed to override PrimeVue defaults */
.custom-menubar .p-menubar-root-list .p-menubar-item .p-button.nav-button,
.custom-menubar .p-menubar-root-list .p-menubar-item button.nav-button,
.custom-menubar .p-menubar-root-list .p-menubar-item .nav-button,
.custom-menubar .p-menubar-root-list .nav-button.p-button,
.custom-menubar .p-menubar-root-list .nav-button {
  background: transparent !important;
}
</style>
